import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import axios from "axios";

function RequestDemoPage() {
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email_id: '',
        mobile_number: '',
        clinic_name: '',
        clinic_type: '',
        additional_information: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const checked = type === 'checkbox' ? e.target.checked : undefined;

        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/demorequests/`, formData, {
                headers: { 'Content-Type': 'application/json' }
            });
    
            if (response.status === 201) {
                navigate('/thank-you');
            }
        } catch (error) {
            setError(error.response?.data?.additional_information || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="py-5 bg-light">
            <Container>
                <Row className="justify-content-center">
                    <Col lg={10}>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-0">
                                <Row className="g-0">
                                    <Col lg={5} style={{ background: 'linear-gradient(135deg, #6cc3f4 0%, #80abe9 50%, #b26dcd 100%)' }} className="text-white p-5 d-flex flex-column justify-content-between">
                                        <div>
                                            <h2 className="display-6 fw-bold mb-4">Experience the Future of Healthcare Management</h2>
                                            <p className="lead mb-5">
                                                Request a personalized demo and see how our platform can transform your practice.
                                            </p>

                                            <div className="mb-4">
                                                <div className="d-flex mb-3">
                                                    <CheckCircle size={20} className="me-2 flex-shrink-0" />
                                                    <p className="mb-0">No credit card required</p>
                                                </div>
                                                <div className="d-flex mb-3">
                                                    <CheckCircle size={20} className="me-2 flex-shrink-0" />
                                                    <p className="mb-0">Personalized 30-minute demo</p>
                                                </div>
                                                <div className="d-flex">
                                                    <CheckCircle size={20} className="me-2 flex-shrink-0" />
                                                    <p className="mb-0">Dedicated support during setup</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-auto">
                                            <p className="mb-0 opacity-75 small">
                                                "VisionThink Tech has completely transformed how we operate our clinic. The time savings alone have been worth the investment."
                                            </p>
                                            <div className="d-flex align-items-center mt-3">
                                                <img
                                                    src="https://storage.googleapis.com/visionthink-tech/dr-samiksha-murkute.webp?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80"
                                                    alt="Dr. Samiksha Murkute"
                                                    className="rounded-circle me-3"
                                                    width="50"
                                                    height="50"
                                                    style={{ objectFit: 'cover' }}
                                                />
                                                <div>
                                                    <p className="mb-0 fw-bold">Dr. Samiksha Murkute</p>
                                                    <p className="mb-0 small">Homeopathic Psychiatric & Counsellor</p>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col lg={7} className="p-5">
                                        <h3 className="fw-bold mb-4" style={{ background: 'linear-gradient(135deg, #6cc3f4 0%, #80abe9 50%, #b26dcd 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Request Your Free Demo</h3>

                                        {error && <Alert variant="danger">{error}</Alert>}

                                        <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group controlId="first_name">
                                                        <Form.Label>First Name*</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="first_name"
                                                            value={formData.first_name}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            Please provide your first name.
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group controlId="last_name">
                                                        <Form.Label>Last Name*</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="last_name"
                                                            value={formData.last_name}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            Please provide your last name.
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group controlId="email_id">
                                                        <Form.Label>Email Address*</Form.Label>
                                                        <Form.Control
                                                            type="email_id"
                                                            name="email_id"
                                                            value={formData.email_id}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            Please provide a valid email_id address.
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group controlId="mobile_number">
                                                        <Form.Label>Mobile Number*</Form.Label>
                                                        <Form.Control
                                                            type="tel"
                                                            name="mobile_number"
                                                            value={formData.mobile_number}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            Please provide your mobile_number number.
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group controlId="clinic_name">
                                                        <Form.Label>Clinic/Practice Name*</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="clinic_name"
                                                            value={formData.clinic_name}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            Please provide your clinic name.
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group controlId="clinic_type">
                                                        <Form.Label>Medical Specialty*</Form.Label>
                                                        <Form.Select
                                                            name="clinic_type"
                                                            value={formData.clinic_type}
                                                            onChange={handleChange}
                                                            required
                                                        >
                                                            <option value="">Select Specialty</option>
                                                            <option value="AL">Allopathy</option>
                                                            <option value="HM">Homeopathy</option>
                                                            <option value="Other">Other</option>
                                                        </Form.Select>
                                                        <Form.Control.Feedback type="invalid">
                                                            Please select your Specialty.
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="mb-3" controlId="additional_information">
                                                <Form.Label>Additional Information (Optional)</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    name="additional_information"
                                                    value={formData.additional_information}
                                                    onChange={handleChange}
                                                    placeholder="Tell us about your specific needs or challenges..."
                                                />
                                            </Form.Group>

                                            <div className="d-grid">
                                                <Button
                                                    variant="primary"
                                                    type="submit"
                                                    size="lg"
                                                    className="rounded-pill"
                                                    disabled={loading}
                                                    style={{ background: 'linear-gradient(135deg, #6cc3f4 0%, #80abe9 50%, #b26dcd 100%)', border: 'none' }}
                                                >
                                                    {loading ? 'Processing...' : 'Request Your Free Demo'}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export default RequestDemoPage;